/**
 * @elutide/shared: the single source of truth for every type crossing a module boundary.
 *
 * Rules for editing this file:
 *  - Only the lead edits it. Agents request changes in INTEGRATION_NOTES.md.
 *  - Every type is derived from a zod schema with z.infer. Never hand write a type twice.
 *  - Schemas are named <Name>Schema and exported alongside the type.
 *  - Money is Cents, an integer. No floats anywhere in a money path.
 */
import { z } from 'zod';
export const CentsSchema = z
    .number()
    .int()
    .transform((n) => n);
export const cents = (n) => {
    if (!Number.isInteger(n))
        throw new Error(`cents() requires an integer, got ${n}`);
    return n;
};
/** Crockford ULID, 26 chars. */
export const UlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'not a ULID');
/** ISO 8601 instant with a Z suffix. */
export const IsoInstantSchema = z.string().datetime({ offset: false });
/** Calendar date in America/New_York, YYYY-MM-DD. */
export const NyDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'not a YYYY-MM-DD date');
export const OPS_TIMEZONE = 'America/New_York';
/* ------------------------------------------------------------------ */
/* Roles and permissions                                               */
/* ------------------------------------------------------------------ */
export const RoleSchema = z.enum(['owner', 'packer', 'agent']);
/**
 * Complete permission set. Must equal the seed in
 * packages/db/seed/0004_roles_permissions.sql. A CI test asserts set equality.
 */
export const PermissionSchema = z.enum([
    // health and shell
    'health.read',
    'home.read',
    'changes.read',
    // auth and users
    'auth.self',
    'users.read',
    'users.manage',
    'users.reset_pin',
    'users.manage_devices',
    'users.manage_passkeys',
    // audit
    'audit.read',
    'audit.read_own',
    'audit.export',
    // inbox
    'inbox.read',
    'inbox.assign',
    'inbox.snooze',
    'inbox.resolve',
    'inbox.resolve_own',
    'inbox.rules_manage',
    // metrics
    'metrics.read',
    'metrics.read_money',
    'metrics.config_manage',
    // orders
    'orders.read',
    'orders.read_packer',
    'orders.read_agent',
    'orders.print',
    'orders.mark_packed',
    'orders.handed_to_ups',
    'orders.hold_place',
    'orders.hold_release',
    'orders.flag_address',
    'orders.edit_shipping_address',
    'orders.edit_lines',
    'orders.edit_customer',
    'orders.mark_paid',
    'orders.cancel',
    'orders.refund',
    'orders.refund.request',
    'orders.comp',
    'orders.comp.request',
    'orders.note_internal',
    'orders.note_customer',
    'orders.reminder_send',
    'orders.extend_autocancel',
    'orders.tracking_edit',
    'orders.bulk',
    'orders.export',
    // stock
    'stock.read',
    'stock.read_packer',
    'stock.read_cost',
    'stock.adjust',
    'stock.adjust_bounded',
    'stock.count_submit',
    'stock.count_approve',
    'stock.set_visible',
    'stock.set_price',
    'stock.set_reorder',
    'stock.export',
    // purchasing
    'po.read',
    'po.manage',
    'po.receive',
    'suppliers.read',
    'suppliers.manage',
    'supplier_prices.read',
    'supplier_prices.manage',
    'batches.read',
    'batches.manage',
    'wac.read',
    // customers
    'customers.read',
    'customers.read_masked',
    'customers.edit',
    'customers.note',
    'customers.tag',
    'customers.reveal_contact',
    'customers.credit',
    'customers.credit.request',
    'customers.message',
    'customers.export',
    // support
    'support.read',
    'support.reply',
    'support.note',
    'support.status',
    'support.assign',
    'support.tag',
    'support.merge',
    'support.bulk',
    'support.macros_manage',
    'support.sla_manage',
    'support.handoff',
    // finance
    'finance.read',
    'finance.ledgers_manage',
    'finance.close_month',
    'finance.export',
    // growth
    'growth.read',
    'growth.annotate',
    'growth.referrals_review',
    'partners.read',
    'partners.manage',
    // settings
    'settings.read',
    'settings.system',
    'settings.controls',
    'settings.alerts',
    'settings.integrations',
    // jobs
    'jobs.read',
    'jobs.run',
]);
/* ------------------------------------------------------------------ */
/* Session, auth, step-up                                              */
/* ------------------------------------------------------------------ */
export const SessionStateSchema = z.enum(['active', 'locked', 'expired', 'revoked']);
/**
 * How the console authenticates. `passkey` is the original design: passkeys primary, PIN
 * only on an enrolled device. `pin` is the packer bench mode (OPS_AUTH_PIN_ONLY=true):
 * handle plus PIN signs in on any device and a PIN satisfies every step-up tier. The web
 * reads it from `GET /auth/session` (signed in) or `GET /auth/mode` (before sign in).
 */
export const AuthModeSchema = z.enum(['pin', 'passkey']);
export const SessionSchema = z.object({
    session_id: UlidSchema,
    user_id: UlidSchema,
    display_name: z.string(),
    role: RoleSchema,
    permissions: z.array(PermissionSchema),
    device_id: UlidSchema.nullable(),
    device_label: z.string().nullable(),
    state: SessionStateSchema,
    trusted_network: z.boolean(),
    created_at: IsoInstantSchema,
    last_seen_at: IsoInstantSchema,
    idle_expires_at: IsoInstantSchema,
    absolute_expires_at: IsoInstantSchema,
    last_step_up_at: IsoInstantSchema.nullable(),
    /** Present on the `GET /auth/session` response so the shell can pick the login and step-up UI. */
    auth_mode: AuthModeSchema.optional(),
});
export const StepUpTierSchema = z.union([z.literal(0), z.literal(2), z.literal(3)]);
export const StepUpMethodSchema = z.enum(['passkey', 'pin']);
export const StepUpSchema = z.object({
    step_up_id: UlidSchema,
    method: StepUpMethodSchema,
    tier: StepUpTierSchema,
    created_at: IsoInstantSchema,
    expires_at: IsoInstantSchema,
});
export const PinUnlockRequestSchema = z.object({ pin: z.string().regex(/^\d{6}$/) });
export const PasskeyLoginBeginRequestSchema = z.object({
    handle: z.string().min(1).max(64).optional(),
});
/** WebAuthn payloads are passed through to @simplewebauthn, so they stay loose here. */
export const WebAuthnPayloadSchema = z.record(z.string(), z.unknown());
export const PasskeySchema = z.object({
    passkey_id: UlidSchema,
    user_id: UlidSchema,
    label: z.string(),
    credential_id: z.string(),
    transports: z.array(z.string()),
    created_at: IsoInstantSchema,
    last_used_at: IsoInstantSchema.nullable(),
});
export const DeviceSchema = z.object({
    device_id: UlidSchema,
    user_id: UlidSchema,
    label: z.string(),
    enrolled_at: IsoInstantSchema,
    last_seen_at: IsoInstantSchema.nullable(),
    last_ip: z.string().nullable(),
    pin_login_enabled: z.boolean(),
    revoked_at: IsoInstantSchema.nullable(),
});
export const OpsUserSchema = z.object({
    user_id: UlidSchema,
    display_name: z.string(),
    handle: z.string(),
    role: RoleSchema,
    wp_user_id: z.number().int().nullable(),
    is_active: z.boolean(),
    created_at: IsoInstantSchema,
    last_login_at: IsoInstantSchema.nullable(),
    device_count: z.number().int(),
    passkey_count: z.number().int(),
    has_pin: z.boolean(),
    locked_until: IsoInstantSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Metric envelope                                                     */
/* ------------------------------------------------------------------ */
export const MetricBasisSchema = z.enum([
    'woo_paid',
    'woo_created',
    'refund_date',
    'ledger',
    'snapshot',
    'estimate',
    'model',
    'manual',
    'qbo',
]);
export const MetricUnitSchema = z.enum([
    'cents',
    'count',
    'ratio',
    'days',
    'units',
    'units_per_day',
    'multiple',
]);
export const WindowKindSchema = z.enum([
    'today',
    '7d',
    '14d',
    '30d',
    '90d',
    '365d',
    'mtd',
    'month',
    'point',
    'custom',
]);
export const MetricWindowSchema = z.object({
    kind: WindowKindSchema,
    /** Inclusive lower bound, UTC instant. Null for point-in-time metrics. */
    start: IsoInstantSchema.nullable(),
    /** Inclusive upper bound, UTC instant. Null for point-in-time metrics. */
    end: IsoInstantSchema.nullable(),
    tz: z.literal(OPS_TIMEZONE),
    label: z.string(),
});
export const MetricEnvelopeSchema = z.object({
    metric_id: z.string(),
    version: z.number().int().positive(),
    value: z.number().nullable(),
    unit: MetricUnitSchema,
    basis: MetricBasisSchema,
    window: MetricWindowSchema,
    as_of: IsoInstantSchema,
    sources: z.array(z.string()),
    dims: z.record(z.string(), z.string()).nullable(),
    partial: z.boolean(),
    coverage: z.number().min(0).max(1).nullable(),
});
export const MetricQuerySchema = z.object({
    ids: z.array(z.string()).min(1).max(50),
    window: WindowKindSchema.default('30d'),
    start: NyDateSchema.optional(),
    end: NyDateSchema.optional(),
    dims: z.record(z.string(), z.string()).optional(),
});
export const FactsDomainSchema = z.enum([
    'orders',
    'stock',
    'products',
    'customers',
    'batches',
    'ledgers',
    'support',
    'snapshots',
]);
/* ------------------------------------------------------------------ */
/* Payments                                                            */
/* ------------------------------------------------------------------ */
export const PaymentMethodSchema = z.enum([
    'card',
    'zelle',
    'venmo',
    'cashapp',
    'comp',
    'store_credit',
    'offline_other',
    'unknown',
]);
export const PaymentMethodMapRowSchema = z.object({
    raw_value: z.string(),
    canonical: PaymentMethodSchema,
    matcher_eligible: z.boolean(),
    notes: z.string().nullable(),
    active: z.boolean(),
});
export const FeeSourceSchema = z.enum(['estimate', 'payout', 'none']);
/* ------------------------------------------------------------------ */
/* Orders                                                              */
/* ------------------------------------------------------------------ */
export const OpsStateSchema = z.enum([
    'created',
    'awaiting_payment',
    'paid',
    'held',
    'printed',
    'packed',
    'shipped',
    'delivered',
    'cancelled',
    'refunded',
    'failed',
]);
export const OrderQueueSchema = z.enum([
    'needs_attention',
    'awaiting_payment',
    'to_print',
    'to_pack',
    'packed',
    'in_transit',
    // Spec 02-orders puts Delivered on the queue rail. Added 2026-09-06 alongside the
    // matching value on the Postgres order_queue enum (migration 0010).
    'delivered',
    'exceptions',
    'all',
]);
export const OrderFlagSchema = z.enum([
    'in_transit',
    'exception_open',
    'reprint_required',
    'address_problem',
    'payment_variance',
    'unmapped_payment_method',
    'writeback_pending',
    'writeback_dead',
    'first_order',
    'safearrival',
    'manual_order',
    'compliance_hold',
    'customer_replied',
    'attempt_cluster',
]);
export const PrintDocSchema = z.enum(['coa', 'label', 'packing_slip']);
export const PrintStateSchema = z.object({
    coa_printed_at: IsoInstantSchema.nullable(),
    label_printed_at: IsoInstantSchema.nullable(),
    packing_slip_printed_at: IsoInstantSchema.nullable(),
    coa_reprints: z.number().int(),
    label_reprints: z.number().int(),
    packing_slip_reprints: z.number().int(),
});
export const AddressSchema = z.object({
    first_name: z.string(),
    last_name: z.string(),
    company: z.string().nullable(),
    address_1: z.string(),
    address_2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    postcode: z.string(),
    country: z.string(),
});
export const OrderLineSchema = z.object({
    line_id: z.number().int(),
    product_id: z.number().int(),
    variation_id: z.number().int().nullable(),
    sku: z.string().nullable(),
    name: z.string(),
    qty: z.number().int(),
    unit_price_cents: CentsSchema,
    line_subtotal_cents: CentsSchema,
    line_total_cents: CentsSchema,
    line_tax_cents: CentsSchema,
    unit_cost_at_sale_cents: CentsSchema.nullable(),
    cost_source: z.enum(['wac', 'opening', 'estimate', 'manual', 'none']),
    lot_number: z.string().nullable(),
});
/** Line as a packer sees it: no money at all. */
export const OrderLinePackerSchema = OrderLineSchema.pick({
    line_id: true,
    product_id: true,
    variation_id: true,
    sku: true,
    name: true,
    qty: true,
    lot_number: true,
});
/** Row in the orders table for owner and agent. */
export const OrderListRowSchema = z.object({
    order_id: z.number().int(),
    order_number: z.string(),
    ops_state: OpsStateSchema,
    woo_status: z.string(),
    flags: z.array(OrderFlagSchema),
    customer_key: z.string(),
    customer_name: z.string(),
    ship_city: z.string().nullable(),
    ship_state: z.string().nullable(),
    item_count: z.number().int(),
    total_cents: CentsSchema,
    payment_method: PaymentMethodSchema,
    created_at: IsoInstantSchema,
    paid_at: IsoInstantSchema.nullable(),
    auto_cancel_at: IsoInstantSchema.nullable(),
    printed_at: IsoInstantSchema.nullable(),
    packed_at: IsoInstantSchema.nullable(),
    shipped_at: IsoInstantSchema.nullable(),
    delivered_at: IsoInstantSchema.nullable(),
    tracking_number: z.string().nullable(),
    print_state: PrintStateSchema,
    woo_date_modified: IsoInstantSchema,
});
/**
 * Row as a packer sees it. Enforced in the serializer, not by the client.
 * No totals, no payment method, no lifetime stats, no contact.
 */
export const OrderListRowPackerSchema = OrderListRowSchema.omit({
    total_cents: true,
    payment_method: true,
}).extend({
    include_welcome_insert: z.boolean(),
});
export const OrderTimelineEntrySchema = z.object({
    at: IsoInstantSchema,
    kind: z.enum(['audit', 'woo_note', 'carrier', 'reminder', 'writeback', 'payment_match']),
    actor: z.string(),
    summary: z.string(),
    detail: z.record(z.string(), z.unknown()).nullable(),
});
export const OrderDetailSchema = OrderListRowSchema.extend({
    lines: z.array(OrderLineSchema),
    subtotal_cents: CentsSchema,
    discount_cents: CentsSchema,
    shipping_cents: CentsSchema,
    fee_cents: CentsSchema,
    tax_cents: CentsSchema,
    refunded_cents: CentsSchema,
    rr_applied_cents: CentsSchema,
    payment_method_raw: z.string().nullable(),
    payment_reference: z.string().nullable(),
    transaction_id: z.string().nullable(),
    processor_fee_cents: CentsSchema.nullable(),
    fee_source: FeeSourceSchema,
    billing: AddressSchema.nullable(),
    shipping: AddressSchema.nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    customer_lifetime_cents: CentsSchema.nullable(),
    customer_order_count: z.number().int().nullable(),
    carrier: z.string().nullable(),
    shipping_service: z.string().nullable(),
    label_cost_cents: CentsSchema.nullable(),
    packed_photo_url: z.string().nullable(),
    packed_by: z.string().nullable(),
    hold_reason: z.string().nullable(),
    attempt_cluster_id: UlidSchema.nullable(),
    support_ticket_refs: z.array(z.string()),
    timeline: z.array(OrderTimelineEntrySchema),
});
export const OrderDetailPackerSchema = OrderDetailSchema.omit({
    total_cents: true,
    payment_method: true,
    lines: true,
    subtotal_cents: true,
    discount_cents: true,
    shipping_cents: true,
    fee_cents: true,
    tax_cents: true,
    refunded_cents: true,
    rr_applied_cents: true,
    payment_method_raw: true,
    payment_reference: true,
    transaction_id: true,
    processor_fee_cents: true,
    fee_source: true,
    billing: true,
    customer_lifetime_cents: true,
    customer_order_count: true,
    label_cost_cents: true,
}).extend({
    lines: z.array(OrderLinePackerSchema),
    include_welcome_insert: z.boolean(),
    email_masked: z.string().nullable(),
    phone_last4: z.string().nullable(),
});
/* Order action requests */
export const HoldReasonSchema = z.enum([
    'identity_verification',
    'restricted_destination',
    'quantity_threshold',
    'payment_verification',
    'suspected_fraud',
    'other',
]);
export const RefundReasonSchema = z.enum([
    'customer_request',
    'damaged_in_transit',
    'lost_in_transit',
    'wrong_item',
    'duplicate_order',
    'price_adjustment',
    'goodwill',
    'other',
]);
export const PaymentVarianceReasonSchema = z.enum([
    'accepted_short',
    'overpaid_credit_issued',
    'overpaid_refunded',
    'fee_deducted_by_sender',
    'partial_payment_expected',
    'other',
]);
export const PriceEditReasonSchema = z.enum([
    'price_match',
    'loyalty',
    'error_correction',
    'promo_honored',
    'other',
]);
export const MarkPaidRequestSchema = z.object({
    expected_version: IsoInstantSchema,
    method: PaymentMethodSchema,
    reference: z.string().min(1).max(128),
    amount_cents: CentsSchema,
    received_at: IsoInstantSchema,
    variance_reason: PaymentVarianceReasonSchema.nullable(),
    note: z.string().max(1000).nullable(),
});
export const LineEditOpSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('add'),
        product_id: z.number().int(),
        variation_id: z.number().int().nullable(),
        qty: z.number().int().positive(),
        unit_price_cents: CentsSchema.nullable(),
    }),
    z.object({ op: z.literal('remove'), line_id: z.number().int() }),
    z.object({ op: z.literal('set_qty'), line_id: z.number().int(), qty: z.number().int().min(0) }),
    z.object({
        op: z.literal('set_price'),
        line_id: z.number().int(),
        unit_price_cents: CentsSchema,
        reason: PriceEditReasonSchema,
    }),
]);
export const LineEditRequestSchema = z.object({
    expected_version: IsoInstantSchema,
    ops: z.array(LineEditOpSchema).min(1).max(50),
    shipping_cents: CentsSchema.nullable(),
    coupon_add: z.array(z.string()).nullable(),
    coupon_remove: z.array(z.string()).nullable(),
    reason: z.string().min(1).max(500),
    allow_oversell: z.boolean().default(false),
});
export const LineEditDiffSchema = z.object({
    before: z.object({
        lines: z.array(OrderLineSchema),
        total_cents: CentsSchema,
        tax_cents: CentsSchema,
    }),
    after: z.object({
        lines: z.array(OrderLineSchema),
        total_cents: CentsSchema,
        tax_cents: CentsSchema,
    }),
    stock_deltas: z.array(z.object({ product_id: z.number().int(), delta: z.number().int() })),
    balance_due_cents: CentsSchema,
    overpaid_cents: CentsSchema,
});
export const RefundRequestSchema = z.object({
    expected_version: IsoInstantSchema,
    amount_cents: CentsSchema.nullable(),
    lines: z
        .array(z.object({ line_id: z.number().int(), qty: z.number().int(), restock: z.boolean() }))
        .nullable(),
    refund_shipping_cents: CentsSchema.nullable(),
    reason: RefundReasonSchema,
    note: z.string().max(1000).nullable(),
    gateway_refund: z.boolean().default(false),
    external_reference: z.string().max(128).nullable(),
});
export const CompOrderRequestSchema = z.object({
    customer_key: z.string(),
    lines: z
        .array(z.object({
        product_id: z.number().int(),
        variation_id: z.number().int().nullable(),
        qty: z.number().int().positive(),
    }))
        .min(1),
    shipping_service: z.string().nullable(),
    reason: z.enum(['replacement', 'goodwill', 'influencer', 'qc_sample', 'other']),
    note: z.string().max(1000).nullable(),
    origin_order_id: z.number().int().nullable(),
    origin_ticket_ref: z.string().nullable(),
});
export const AddressEditRequestSchema = z.object({
    expected_version: IsoInstantSchema,
    shipping: AddressSchema,
    reason: z.string().min(1).max(500),
    void_and_relabel: z.boolean().default(false),
});
/**
 * `PUT /orders/:orderId/address`, the rebuilt drawer's Customer > Edit address.
 *
 * Distinct from AddressEditRequestSchema, which is the older shipping-only route that
 * carries the void-and-relabel confirmation. This one edits either side or both, and it
 * refuses outright once the carrier has the parcel (`order.already_shipped`) rather than
 * offering a relabel, because there is nothing left to relabel.
 */
export const OrderAddressEditRequestSchema = z
    .object({
    expected_version: IsoInstantSchema,
    billing: AddressSchema.optional(),
    shipping: AddressSchema.optional(),
    reason: z.string().min(1).max(500),
})
    .refine((v) => v.billing !== undefined || v.shipping !== undefined, {
    message: 'Send a billing address, a shipping address, or both.',
    path: ['shipping'],
});
/** What the address edit returns, and what phase 3 renders after a successful save. */
export const OrderAddressEditResultSchema = z.object({
    order_id: z.number().int(),
    billing: AddressSchema.nullable(),
    shipping: AddressSchema.nullable(),
    changed: z.array(z.enum(['billing', 'shipping'])),
});
/* ------------------------------------------------------------------ */
/* Orders phase 2e: reprint, auto-cancel extend, credit                */
/* ------------------------------------------------------------------ */
/**
 * `POST /orders/:orderId/reprint`, the drawer's Reprint action and the bulk fan-out.
 *
 * The Idempotency-Key header, not a body field, becomes the bridge `command_id`. There is
 * one key per logical reprint, so a retry after a timeout replays instead of printing a
 * second label, and the same key is what the `idempotency_keys` row is written under.
 */
export const OrderReprintRequestSchema = z.object({
    docs: z.array(PrintDocSchema).min(1).max(3),
});
export const OrderReprintResultSchema = z.object({
    order_id: z.number().int(),
    /** The bridge command id, which is the Idempotency-Key the caller sent. */
    command_id: z.string(),
    requested: z.array(PrintDocSchema),
    /** Documents the print queue accepted. Print state advances for these and no others. */
    accepted: z.array(PrintDocSchema),
    failed: z.array(z.object({ doc: PrintDocSchema, reason: z.string() })),
    /** True when the bridge recognised the command id and queued nothing a second time. */
    replayed: z.boolean(),
    print_state: PrintStateSchema,
});
/**
 * `POST /orders/:orderId/autocancel/extend`.
 *
 * `hours` is optional: omitted, it takes the `orders.autocancel_extend_hours` row from
 * metric_config, so the owner can change the increment without a deploy. 72 is the ceiling
 * on both sides of the bridge.
 */
export const AUTOCANCEL_EXTEND_MAX_HOURS = 72;
export const AutoCancelExtendRequestSchema = z.object({
    hours: z.number().int().positive().max(AUTOCANCEL_EXTEND_MAX_HOURS).optional(),
    note: z.string().max(500).nullable().default(null),
});
export const AutoCancelExtendResultSchema = z.object({
    order_id: z.number().int(),
    hours: z.number().int(),
    previous_autocancel_at: IsoInstantSchema.nullable(),
    autocancel_at: IsoInstantSchema,
});
/**
 * `POST /orders/:orderId/credit`, Research Rewards from the order drawer.
 *
 * The customer is resolved from the order, never sent by the client: a body that named its
 * own customer would let a caller with `orders.write` credit an arbitrary account.
 */
export const OrderCreditRequestSchema = z.object({
    amount_cents: z.number().int().positive(),
    reason: z.string().min(1).max(500),
});
export const OrderCreditResultSchema = z.object({
    order_id: z.number().int(),
    customer_key: z.string(),
    amount_cents: CentsSchema,
    reason: z.string(),
    granted_at: IsoInstantSchema,
});
/**
 * What the bridge reports about an order in ShipStation. Read only.
 *
 * `label_voided` is true only when there were shipments and every one of them is voided.
 * Zero shipments leaves it false with `shipment_count` 0, because "never had a label" and
 * "had a label and voided it" are different answers to the question cancel is asking.
 */
export const ShipStationStatusSchema = z.object({
    order_id: z.number().int(),
    found: z.boolean(),
    status: z.string().nullable(),
    label_voided: z.boolean(),
    shipstation_order_id: z.number().int().nullable(),
    tracking_number: z.string().nullable(),
    shipment_count: z.number().int(),
    live_shipment_count: z.number().int(),
    /** Set when ShipStation could not be read. The caller decides what that means. */
    lookup_error: z.string().nullable(),
});
export const MarkPackedRequestSchema = z.object({
    photo_upload_id: UlidSchema.nullable(),
    photo_override_reason: z.string().max(500).nullable(),
    lot_numbers: z.array(z.object({ line_id: z.number().int(), lot: z.string() })).nullable(),
});
export const ReminderChannelSchema = z.enum(['email', 'sms']);
export const ReminderSchema = z.object({
    reminder_id: UlidSchema,
    order_id: z.number().int(),
    step: z.enum(['h12', 'h20', 'manual']),
    channel: ReminderChannelSchema,
    template: z.string(),
    scheduled_at: IsoInstantSchema,
    sent_at: IsoInstantSchema.nullable(),
    delivery_status: z.enum(['queued', 'sent', 'delivered', 'bounced', 'failed', 'cancelled']),
    provider_message_id: z.string().nullable(),
});
export const AttemptClusterSchema = z.object({
    cluster_id: UlidSchema,
    identity_key: z.string(),
    order_ids: z.array(z.number().int()),
    opened_at: IsoInstantSchema,
    kind: z.enum(['possible_double', 'double_paid', 'card_failure_burst']),
    dismissed_at: IsoInstantSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Stock                                                               */
/* ------------------------------------------------------------------ */
export const StockStatusSchema = z.enum(['ok', 'low', 'critical', 'out', 'suppressed']);
export const ProductFamilySchema = z.enum([
    'glp',
    'glow',
    'klow',
    'bpc',
    'tb',
    'reconstitution',
    'other',
]);
/** Owner and agent view of a stock row. */
export const StockRowSchema = z.object({
    product_id: z.number().int(),
    parent_id: z.number().int().nullable(),
    sku: z.string().nullable(),
    name: z.string(),
    size_label: z.string().nullable(),
    family: ProductFamilySchema,
    is_bundle: z.boolean(),
    is_visible: z.boolean(),
    available_qty: z.number().int(),
    committed_qty: z.number().int(),
    shelf_qty: z.number().int(),
    units_30d: z.number().int(),
    velocity_30d: z.number(),
    days_left: z.number().int().nullable(),
    days_left_with_inbound: z.number().int().nullable(),
    inbound_qty: z.number().int(),
    inbound_eta: NyDateSchema.nullable(),
    price_cents: CentsSchema,
    regular_price_cents: CentsSchema,
    wac_cents: CentsSchema.nullable(),
    wac_source: z.enum(['wac', 'opening', 'estimate', 'manual', 'none']),
    gross_margin_pct: z.number().nullable(),
    alt_source_cents: CentsSchema.nullable(),
    status: StockStatusSchema,
});
/** Packer view: quantities only, no money. */
export const StockRowPackerSchema = StockRowSchema.omit({
    price_cents: true,
    regular_price_cents: true,
    wac_cents: true,
    wac_source: true,
    gross_margin_pct: true,
    alt_source_cents: true,
});
export const StockAdjustReasonSchema = z.enum([
    'count_correction',
    'breakage',
    'qc_sample',
    'giveaway_unlogged',
    'prior_miscount',
    'receiving_unlogged',
    'loss_or_theft',
    'found',
    'sync_variance',
    'other',
]);
export const StockAdjustRequestSchema = z.object({
    product_id: z.number().int(),
    expected_qty: z.number().int(),
    delta: z.number().int(),
    reason: StockAdjustReasonSchema,
    note: z.string().max(500).nullable(),
    photo_upload_id: UlidSchema.nullable(),
});
export const CostCategorySchema = z.enum([
    'goods',
    'freight',
    'duties_customs',
    'testing',
    'labeling',
    'payment_fees',
    'other',
]);
export const BatchStatusSchema = z.enum([
    'quarantined',
    'available',
    'depleted',
    'opening_balance',
    'cancelled',
]);
export const BatchSchema = z.object({
    batch_id: UlidSchema,
    product_id: z.number().int(),
    po_id: UlidSchema.nullable(),
    po_line_id: UlidSchema.nullable(),
    supplier_id: UlidSchema.nullable(),
    lot_number: z.string().nullable(),
    qty_received: z.number().int(),
    qty_remaining: z.number().int(),
    received_at: NyDateSchema.nullable(),
    available_at: NyDateSchema.nullable(),
    unit_goods_cents: CentsSchema,
    freight_alloc_cents: CentsSchema,
    duties_alloc_cents: CentsSchema,
    testing_alloc_cents: CentsSchema,
    labeling_alloc_cents: CentsSchema,
    payment_fees_alloc_cents: CentsSchema,
    other_alloc_cents: CentsSchema,
    landed_unit_cents: CentsSchema,
    cost_status: z.enum(['provisional', 'final']),
    status: BatchStatusSchema,
    coa_url: z.string().nullable(),
});
export const WacEventTypeSchema = z.enum([
    'receipt',
    'sale',
    'restock',
    'adjustment',
    'cost_revision',
    'opening_balance',
]);
export const WacLedgerEntrySchema = z.object({
    entry_id: UlidSchema,
    product_id: z.number().int(),
    event_type: WacEventTypeSchema,
    event_ref: z.string().nullable(),
    event_date: NyDateSchema,
    qty_delta: z.number().int(),
    unit_cost_cents: CentsSchema.nullable(),
    on_hand_after: z.number().int(),
    wac_after_cents: CentsSchema,
    created_at: IsoInstantSchema,
});
export const PoStatusSchema = z.enum([
    'draft',
    'ordered',
    'paid',
    'in_transit',
    'customs',
    'received',
    'qc',
    'available',
    'closed',
    'cancelled',
]);
export const PurchaseOrderSchema = z.object({
    po_id: UlidSchema,
    po_number: z.string(),
    supplier_id: UlidSchema,
    supplier_name: z.string(),
    status: PoStatusSchema,
    currency: z.string(),
    fx_rate: z.number().nullable(),
    ordered_at: NyDateSchema.nullable(),
    expected_at: NyDateSchema.nullable(),
    received_at: NyDateSchema.nullable(),
    goods_cents: CentsSchema,
    freight_cents: CentsSchema,
    duties_cents: CentsSchema,
    other_cents: CentsSchema,
    total_landed_cents: CentsSchema,
    paid_to_date_cents: CentsSchema,
    tracking: z.string().nullable(),
    notes: z.string().nullable(),
});
export const PoLineSchema = z.object({
    po_line_id: UlidSchema,
    po_id: UlidSchema,
    product_id: z.number().int(),
    sku: z.string().nullable(),
    qty_ordered: z.number().int(),
    qty_received: z.number().int(),
    unit_cost_cents: CentsSchema,
    line_total_cents: CentsSchema,
});
export const SupplierSchema = z.object({
    supplier_id: UlidSchema,
    name: z.string(),
    contact_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    country: z.string().nullable(),
    default_lead_days: z.number().int(),
    default_qc_days: z.number().int(),
    payment_terms: z.string().nullable(),
    currency: z.string(),
    is_active: z.boolean(),
    lead_days_median: z.number().nullable(),
    lead_days_p80: z.number().nullable(),
    lead_sample_n: z.number().int(),
});
export const ReorderSuggestionSchema = z.object({
    product_id: z.number().int(),
    sku: z.string().nullable(),
    name: z.string(),
    velocity_30d: z.number(),
    available_qty: z.number().int(),
    inbound_qty: z.number().int(),
    stockout_date: NyDateSchema.nullable(),
    cover_point_days: z.number().int(),
    suggested_qty: z.number().int(),
    urgency: z.enum(['normal', 'high', 'critical']),
    preferred_supplier_id: UlidSchema.nullable(),
    snoozed_until: NyDateSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Customers                                                           */
/* ------------------------------------------------------------------ */
export const CustomerSegmentSchema = z.enum(['whale', 'regular', 'one_timer', 'lapsed']);
export const CustomerListRowSchema = z.object({
    customer_key: z.string(),
    wp_user_id: z.number().int().nullable(),
    display_name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    orders_paid: z.number().int(),
    lifetime_net_cents: CentsSchema,
    first_paid_at: IsoInstantSchema.nullable(),
    last_paid_at: IsoInstantSchema.nullable(),
    rr_balance_cents: CentsSchema,
    segment: CustomerSegmentSchema,
    tags: z.array(z.string()),
    is_test: z.boolean(),
});
/** Agent view. Contact is masked until an audited reveal. */
export const CustomerListRowMaskedSchema = CustomerListRowSchema.omit({
    email: true,
    phone: true,
    lifetime_net_cents: true,
    rr_balance_cents: true,
}).extend({
    email_masked: z.string().nullable(),
    phone_last4: z.string().nullable(),
});
export const CustomerNoteSchema = z.object({
    note_id: UlidSchema,
    customer_key: z.string(),
    author_user_id: UlidSchema,
    author_name: z.string(),
    body: z.string(),
    pinned: z.boolean(),
    created_at: IsoInstantSchema,
});
export const CustomerDetailSchema = CustomerListRowSchema.extend({
    billing: AddressSchema.nullable(),
    shipping: AddressSchema.nullable(),
    segment_reason: z.string(),
    reorder_cadence_days: z.number().nullable(),
    notes: z.array(CustomerNoteSchema),
    recent_order_ids: z.array(z.number().int()),
    support_ticket_refs: z.array(z.string()),
    sms_opt_in: z.boolean(),
    email_opt_in: z.boolean(),
});
export const RrLedgerEntrySchema = z.object({
    entry_id: z.string(),
    customer_key: z.string(),
    at: IsoInstantSchema,
    delta_cents: CentsSchema,
    balance_after_cents: CentsSchema,
    kind: z.enum(['earn', 'redeem', 'expire', 'admin_increase', 'admin_decrease']),
    order_id: z.number().int().nullable(),
    reason: z.string().nullable(),
    actor: z.string().nullable(),
});
export const CommunicationChannelSchema = z.enum([
    'email',
    'sms',
    'woo_note',
    'support_reply',
    'slack',
]);
export const CommunicationLogEntrySchema = z.object({
    comm_id: UlidSchema,
    customer_key: z.string(),
    channel: CommunicationChannelSchema,
    direction: z.enum(['outbound', 'inbound']),
    template: z.string().nullable(),
    subject: z.string().nullable(),
    ref_type: z.string().nullable(),
    ref_id: z.string().nullable(),
    sent_by: z.string().nullable(),
    sent_at: IsoInstantSchema,
    delivery_status: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* Support                                                             */
/* ------------------------------------------------------------------ */
export const TicketStatusSchema = z.enum(['new', 'in_progress', 'waiting', 'resolved']);
export const TicketSourceSchema = z.enum(['web', 'email', 'sms', 'agent', 'bot']);
export const TicketPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const SupportQueueSchema = z.enum([
    'needs_reply',
    'waiting_on_customer',
    'live_handoff',
    'unassigned',
    'resolved_7d',
    'all',
]);
export const SlaStateSchema = z.enum(['ok', 'at_risk', 'breached', 'none']);
export const TicketListRowSchema = z.object({
    ticket_id: z.number().int(),
    public_ref: z.string(),
    status: TicketStatusSchema,
    priority: TicketPrioritySchema,
    source: TicketSourceSchema,
    category: z.string().nullable(),
    subject: z.string(),
    customer_key: z.string().nullable(),
    customer_display: z.string(),
    is_guest: z.boolean(),
    assignee_user_id: UlidSchema.nullable(),
    assignee_name: z.string().nullable(),
    order_id: z.number().int().nullable(),
    order_verified: z.boolean(),
    item_count: z.number().int(),
    tags: z.array(z.string()),
    reopened_count: z.number().int(),
    merged_into_ticket_id: z.number().int().nullable(),
    sla_state: SlaStateSchema,
    sla_first_due_at: IsoInstantSchema.nullable(),
    sla_resolve_due_at: IsoInstantSchema.nullable(),
    first_response_at: IsoInstantSchema.nullable(),
    last_customer_msg_at: IsoInstantSchema.nullable(),
    last_agent_msg_at: IsoInstantSchema.nullable(),
    created_at: IsoInstantSchema,
    updated_at: IsoInstantSchema,
});
export const TicketMessageSchema = z.object({
    message_id: z.number().int(),
    kind: z.enum(['customer', 'agent_reply', 'internal_note', 'system']),
    author: z.string(),
    body: z.string(),
    channel: z.enum(['web', 'email', 'sms', 'system']).nullable(),
    delivery_status: z
        .enum(['queued', 'sent', 'delivered', 'bounced', 'failed', 'cancelled'])
        .nullable(),
    attachments: z.array(z.object({ id: z.number().int(), name: z.string(), size: z.number() })),
    created_at: IsoInstantSchema,
});
export const TicketDetailSchema = TicketListRowSchema.extend({
    body: z.string(),
    customer_email_masked: z.string().nullable(),
    customer_phone_last4: z.string().nullable(),
    messages: z.array(TicketMessageSchema),
    links: z.array(z.object({
        link_type: z.enum(['order', 'comp_order', 'refund', 'rr_credit', 'handoff', 'ticket']),
        target_id: z.string(),
        created_at: IsoInstantSchema,
    })),
    handoff_id: z.number().int().nullable(),
});
export const SupportReplyRequestSchema = z.object({
    body: z.string().min(1).max(10_000),
    channel: z.enum(['email', 'sms']).default('email'),
    macro_id: UlidSchema.nullable(),
    set_status: TicketStatusSchema.nullable(),
});
export const SupportBulkActionSchema = z.enum([
    'resolve',
    'set_status',
    'assign',
    'set_priority',
    'set_category',
    'add_tag',
    'remove_tag',
    'merge',
    'reply',
]);
export const SupportBulkRequestSchema = z.object({
    action: SupportBulkActionSchema,
    ticket_ids: z.array(z.number().int()).min(1).max(50),
    params: z.record(z.string(), z.unknown()),
    notify: z.boolean().default(false),
    reason: z.string().max(500).nullable(),
});
export const SupportBulkJobSchema = z.object({
    job_id: UlidSchema,
    action: SupportBulkActionSchema,
    ticket_count: z.number().int(),
    status: z.enum(['queued', 'running', 'done', 'undone', 'cancelled', 'partial']),
    undo_until: IsoInstantSchema.nullable(),
    created_at: IsoInstantSchema,
    finished_at: IsoInstantSchema.nullable(),
});
export const SupportMacroSchema = z.object({
    macro_id: UlidSchema,
    name: z.string(),
    shortcut: z.string().nullable(),
    body_email: z.string(),
    body_sms: z.string().nullable(),
    set_status: TicketStatusSchema.nullable(),
    set_priority: TicketPrioritySchema.nullable(),
    set_category: z.string().nullable(),
    add_tags: z.array(z.string()),
    version: z.number().int(),
    is_active: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Action inbox                                                        */
/* ------------------------------------------------------------------ */
export const SeveritySchema = z.enum(['info', 'warn', 'crit']);
export const InboxStateSchema = z.enum(['open', 'assigned', 'snoozed', 'resolved']);
export const InboxItemSchema = z.object({
    item_id: UlidSchema,
    code: z.string(),
    severity: SeveritySchema,
    title: z.string(),
    context: z.string().nullable(),
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    dedupe_key: z.string(),
    state: InboxStateSchema,
    assignee_user_id: UlidSchema.nullable(),
    snooze_until: IsoInstantSchema.nullable(),
    owner_only: z.boolean(),
    primary_action: z
        .object({ label: z.string(), href: z.string(), method: z.string().nullable() })
        .nullable(),
    created_at: IsoInstantSchema,
    updated_at: IsoInstantSchema,
    resolved_at: IsoInstantSchema.nullable(),
    resolution: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* Audit                                                               */
/* ------------------------------------------------------------------ */
export const AuditResultSchema = z.enum(['success', 'denied', 'error', 'queued', 'cancelled']);
export const AuditRowSchema = z.object({
    audit_id: z.string(),
    ts: IsoInstantSchema,
    request_id: UlidSchema.nullable(),
    actor_user_id: UlidSchema.nullable(),
    actor_role: RoleSchema.nullable(),
    actor_system: z.string().nullable(),
    ip: z.string().nullable(),
    device_id: UlidSchema.nullable(),
    action: z.string(),
    tier: StepUpTierSchema,
    object_type: z.string().nullable(),
    object_id: z.string().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    reason: z.string().nullable(),
    result: AuditResultSchema,
    batch_id: UlidSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */
// 'not_configured' is a fourth colourless state: the input for this check was never
// wired up. It is not a failure, it never counts as an issue and it is never aged.
export const HealthStateSchema = z.enum(['ok', 'warn', 'crit', 'unknown', 'not_configured']);
export const HealthCheckSchema = z.object({
    check: z.string(),
    state: HealthStateSchema,
    label: z.string(),
    value: z.string().nullable(),
    checked_at: IsoInstantSchema,
});
export const HealthResponseSchema = z.object({
    state: HealthStateSchema,
    checks: z.array(HealthCheckSchema),
    as_of: IsoInstantSchema,
});
/* ------------------------------------------------------------------ */
/* Partners and attribution                                            */
/* ------------------------------------------------------------------ */
export const PartnerAttributionSchema = z.object({
    attribution_id: UlidSchema,
    order_id: z.number().int(),
    partner_slug: z.string(),
    subid: z.string().nullable(),
    clickid: z.string().nullable(),
    clicked_at: IsoInstantSchema.nullable(),
    landing_url: z.string().nullable(),
    matched_rule: z.enum(['cookie', 'coupon', 'utm', 'manual']),
    eligible: z.boolean(),
    ineligible_reason: z.string().nullable(),
    created_at: IsoInstantSchema,
});
/* ------------------------------------------------------------------ */
/* Finance ledgers                                                     */
/* ------------------------------------------------------------------ */
export const FixedCostCategorySchema = z.enum([
    'payroll',
    'contractor',
    'saas',
    'tools',
    'rent',
    'insurance',
    'professional_fees',
    'supplies',
    'debt_service',
    'owner_draw',
    'taxes_estimated',
    'other',
]);
export const FixedCostFrequencySchema = z.enum([
    'weekly',
    'biweekly',
    'monthly',
    'quarterly',
    'annual',
    'one_time',
]);
export const FixedCostSchema = z.object({
    fixed_cost_id: UlidSchema,
    name: z.string(),
    category: FixedCostCategorySchema,
    amount_cents: CentsSchema,
    frequency: FixedCostFrequencySchema,
    anchor_day: z.number().int().min(1).max(31).nullable(),
    anchor_date: NyDateSchema.nullable(),
    start_date: NyDateSchema,
    end_date: NyDateSchema.nullable(),
    vendor: z.string().nullable(),
    payment_source: z.string().nullable(),
    include_in_pnl: z.boolean(),
    include_in_cash_forecast: z.boolean(),
    monthly_equivalent_cents: CentsSchema,
    active: z.boolean(),
    notes: z.string().nullable(),
});
export const HeldFundStatusSchema = z.enum([
    'held',
    'partially_released',
    'released',
    'forfeited',
]);
export const HeldFundSchema = z.object({
    held_fund_id: UlidSchema,
    processor: z.string(),
    payout_id: z.string().nullable(),
    reference: z.string().nullable(),
    amount_held_cents: CentsSchema,
    released_cents: CentsSchema,
    forfeited_cents: CentsSchema,
    held_date: NyDateSchema,
    expected_release_date: NyDateSchema.nullable(),
    actual_release_date: NyDateSchema.nullable(),
    status: HeldFundStatusSchema,
    source: z.enum(['statement_import', 'manual']),
    notes: z.string().nullable(),
});
export const TaxFilingStatusSchema = z.enum(['open', 'accrued', 'filed', 'paid', 'late']);
export const TaxFilingSchema = z.object({
    tax_filing_id: UlidSchema,
    jurisdiction: z.string(),
    filing_frequency: z.enum(['monthly', 'quarterly', 'annual']),
    period_start: NyDateSchema,
    period_end: NyDateSchema,
    due_date: NyDateSchema,
    collected_cents: CentsSchema,
    adjustments_cents: CentsSchema,
    adjustment_reason: z.string().nullable(),
    remitted_cents: CentsSchema,
    filed_date: NyDateSchema.nullable(),
    paid_date: NyDateSchema.nullable(),
    confirmation_number: z.string().nullable(),
    status: TaxFilingStatusSchema,
    notes: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* API envelope: pagination, errors, changes                           */
/* ------------------------------------------------------------------ */
export const PaginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(2048).optional(),
    sort: z.string().max(200).optional(),
});
export const pageSchema = (item) => z.object({
    items: z.array(item),
    next_cursor: z.string().nullable(),
    total_estimate: z.number().int().nullable(),
});
export const ErrorCodeSchema = z.enum([
    'validation_failed',
    'idempotency_key_required',
    'idempotency_key_reused',
    'unauthenticated',
    'session_locked',
    'forbidden',
    'step_up_required',
    'typed_confirm_required',
    'rate_limited',
    'not_found',
    'version_conflict',
    'stock_conflict',
    'wp_unavailable',
    'wp_rejected',
    'wp_disabled',
    'dependency_unavailable',
    'internal',
    // Added 2026-09-06 for the rebuilt Orders drawer. Both are 409s that mean "the parcel
    // has moved on": a bought ShipStation label that is still live, and an order the carrier
    // already has. They are separate codes because the operator's next step differs, and the
    // drawer shows a different remedy for each.
    'order.label_active',
    'order.already_shipped',
    // Added 2026-09-06, phase 2e. Both are 409s about an order that cannot take the action
    // asked of it, and both need their own remedy in the drawer: a guest order has no
    // Research Rewards account to credit, and an order with no live auto-cancel action has
    // no deadline to push out.
    'order.no_customer',
    'order.autocancel_not_pending',
    // Added 2026-09-19 for the Packer Bench. A 409 from the vial queue: the job is no longer
    // `queued` (the agent claimed it, or it already finished or was cancelled), so it cannot
    // be cancelled now.
    'bench.vial_not_queued',
    // Added 2026-09-19. A 409 from enqueue: the plugin already holds a job under this
    // `source_ref` that a different user requested. `source_ref` is `<user_id>:<key>`, so
    // this cannot happen through the API and means a forged or replayed reference.
    'bench.vial_ref_conflict',
]);
export const ApiErrorSchema = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    code: ErrorCodeSchema,
    detail: z.string(),
    instance: z.string(),
    request_id: z.string(),
    errors: z
        .array(z.object({ field: z.string(), code: z.string(), message: z.string() }))
        .optional(),
});
export const OpsEventTypeSchema = z.enum([
    'order.changed',
    'order.writeback.failed',
    'stock.changed',
    'product.changed',
    'customer.changed',
    'support.ticket.changed',
    'metric.invalidated',
    'inbox.changed',
    'snapshot.completed',
    'job.failed',
    'audit.written',
    'security.event',
]);
export const OpsEventSchema = z.object({
    event_id: z.number().int(),
    ts: IsoInstantSchema,
    type: OpsEventTypeSchema,
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
    money: z.boolean(),
});
export const ChangesResponseSchema = z.object({
    events: z.array(OpsEventSchema),
    cursor: z.number().int(),
    facts_version: z.record(z.string(), z.number().int()),
});
/* ------------------------------------------------------------------ */
/* Settings and controls                                               */
/* ------------------------------------------------------------------ */
export const ControlIdSchema = z.enum(['gate', 'bogo']);
export const ControlStateSchema = z.object({
    control: ControlIdSchema,
    enabled: z.boolean(),
    last_toggled_at: IsoInstantSchema.nullable(),
    last_toggled_by: z.string().nullable(),
    cooldown_until: IsoInstantSchema.nullable(),
    verified_at: IsoInstantSchema.nullable(),
    verification_ok: z.boolean().nullable(),
});
export const ControlToggleRequestSchema = z.object({
    enabled: z.boolean(),
    typed_confirm: z.enum(['ON', 'OFF']),
    reason: z.string().min(1).max(500),
});
export const JobRunSchema = z.object({
    run_id: UlidSchema,
    job_name: z.string(),
    started_at: IsoInstantSchema,
    finished_at: IsoInstantSchema.nullable(),
    status: z.enum(['running', 'ok', 'failed', 'timeout', 'skipped']),
    attempt: z.number().int(),
    duration_ms: z.number().int().nullable(),
    rows: z.number().int().nullable(),
    error: z.string().nullable(),
});
//# sourceMappingURL=types.js.map